import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, CheckCircle2 } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { listRentals, upsertRental, deleteRental, findRentalConflict, listCustomers, MIGRATION_MISSING } from "../../../lib/adminApi";
import { MigrationNotice } from "../../../components/admin/MigrationNotice";

const inputCls =
  "w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-3 py-2.5 text-sm text-[#404040] focus:outline-none focus:border-[#B09060]";
const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-[#8F7648] mb-1";

const RENTAL_STATUS = ["aktif", "tamamlandı", "iptal"];
const STATUS_STYLE = {
  "aktif": "bg-emerald-50 text-emerald-700",
  "tamamlandı": "bg-slate-100 text-slate-600",
  "iptal": "bg-red-50 text-red-600",
};
const EMPTY = { vehicle_id: "", customer_id: "", start_date: "", end_date: "", monthly_rate: "", status: "aktif", notes: "" };
const fmt = (d) => new Date(d).toLocaleDateString("tr-TR");

export default function RentalsSection() {
  const [rentals, setRentals] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | missing
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([listRentals(), listCustomers()]);
      const { data: v } = await supabase.from("vehicles").select("id, name, status");
      setRentals(r);
      setCustomers(c);
      setVehicles(v || []);
      setState("ready");
    } catch (e) {
      setState(e === MIGRATION_MISSING ? "missing" : "ready");
      if (e !== MIGRATION_MISSING) toast.error("Kiralamalar yüklenemedi.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    if (editing.end_date < editing.start_date) {
      toast.error("Bitiş tarihi başlangıçtan önce olamaz.");
      return;
    }
    setSaving(true);
    try {
      const conflict = await findRentalConflict(editing.vehicle_id, editing.start_date, editing.end_date, editing.id);
      if (conflict) {
        toast.error(`Bu araç ${fmt(conflict.start_date)} – ${fmt(conflict.end_date)} arasında zaten kirada.`);
        setSaving(false);
        return;
      }
      const row = {
        vehicle_id: editing.vehicle_id,
        customer_id: editing.customer_id,
        start_date: editing.start_date,
        end_date: editing.end_date,
        monthly_rate: Number(editing.monthly_rate),
        status: editing.status,
        notes: editing.notes || null,
      };
      await upsertRental(editing.id ? { ...row, id: editing.id } : row);
      // Araç durumunu otomatik güncelle
      await supabase
        .from("vehicles")
        .update({ status: row.status === "aktif" ? "kirada" : "müsait" })
        .eq("id", row.vehicle_id);
      toast.success(editing.id ? "Kiralama güncellendi." : "Kiralama oluşturuldu.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(`Kaydedilemedi: ${err.message || err}`);
    }
    setSaving(false);
  }

  async function remove(r) {
    if (!window.confirm("Bu kiralama kaydı silinsin mi?")) return;
    try {
      await deleteRental(r.id);
      toast.success("Kiralama silindi.");
      setRentals((rs) => rs.filter((x) => x.id !== r.id));
    } catch (err) {
      toast.error(err.message || "Silinemedi.");
    }
  }

  if (state === "missing") return <MigrationNotice section="Kiralama Yönetimi" />;
  if (state === "loading") return <div className="text-[#6B6B6B] text-sm py-10" data-testid="rentals-loading">Yükleniyor...</div>;

  const aktif = rentals.filter((r) => r.status === "aktif");
  const diger = rentals.filter((r) => r.status !== "aktif");

  function RentalRow({ r }) {
    return (
      <div key={r.id} data-testid={`rental-row-${r.id}`} className="bg-white border border-[#B09060]/20 rounded-sm p-5 flex flex-wrap items-center gap-4 shadow-[0_4px_20px_rgba(176,144,96,0.05)]">
        <div className="flex-1 min-w-52">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-[#404040] text-sm">{r.vehicles?.name || "Araç"}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[r.status]}`}>{r.status}</span>
          </div>
          <div className="text-xs text-[#6B6B6B] mt-1">
            {r.customers?.name || "Müşteri"} · {fmt(r.start_date)} – {fmt(r.end_date)} · {new Intl.NumberFormat("tr-TR").format(r.monthly_rate)} TL/ay
          </div>
          {r.notes && <div className="text-xs text-[#9B9B8F] mt-1">{r.notes}</div>}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEditing({ ...EMPTY, ...r })}
            data-testid={`rental-edit-${r.id}`}
            className="px-3.5 py-2 rounded-sm border border-[#B09060]/30 text-[#8F7648] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors"
          >
            Düzenle
          </button>
          <button
            onClick={() => remove(r)}
            data-testid={`rental-delete-${r.id}`}
            className="p-2 rounded-sm border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="rentals-section">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="font-serif-display text-2xl text-[#404040]">Kiralama Yönetimi</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">{aktif.length} aktif · {diger.length} geçmiş kayıt</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="rental-add-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#B09060] text-white text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#8F7648] transition-colors"
        >
          <Plus className="w-4 h-4" /> Yeni Kiralama
        </button>
      </div>

      {rentals.length === 0 ? (
        <div className="text-center py-16 text-[#6B6B6B]" data-testid="rentals-empty">
          <CheckCircle2 className="w-10 h-10 mx-auto text-[#B09060]/40" strokeWidth={1.25} />
          <p className="mt-3 text-sm">Henüz kiralama kaydı yok.</p>
        </div>
      ) : (
        <>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8F7648] mt-8 mb-3">Aktif Kiralamalar</h3>
          <div className="space-y-3">{aktif.map((r) => <RentalRow key={r.id} r={r} />)}</div>
          {diger.length > 0 && (
            <>
              <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8F7648] mt-8 mb-3">Tamamlanan / İptal</h3>
              <div className="space-y-3 opacity-80">{diger.map((r) => <RentalRow key={r.id} r={r} />)}</div>
            </>
          )}
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="rental-edit-dialog">
          <form onSubmit={save} className="w-full max-w-lg bg-white border border-[#B09060]/20 rounded-sm p-7 space-y-4 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl text-[#404040]">{editing.id ? "Kiralamayı Düzenle" : "Yeni Kiralama"}</h3>
              <button type="button" onClick={() => setEditing(null)} data-testid="rental-dialog-close" className="text-[#9B9B8F] hover:text-[#404040]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className={labelCls}>Araç *</label>
              <select required data-testid="rental-form-vehicle" className={inputCls} value={editing.vehicle_id}
                onChange={(e) => {
                  const v = vehicles.find((x) => x.id === e.target.value);
                  setEditing({ ...editing, vehicle_id: e.target.value, monthly_rate: editing.monthly_rate || (v?.price_amount ?? "") });
                }}>
                <option value="">Seçin...</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}{v.status === "kirada" && editing.vehicle_id !== v.id ? " (kirada)" : ""}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Müşteri *</label>
              <select required data-testid="rental-form-customer" className={inputCls} value={editing.customer_id}
                onChange={(e) => setEditing({ ...editing, customer_id: e.target.value })}>
                <option value="">Seçin...</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
              </select>
              {customers.length === 0 && (
                <p className="text-[11px] text-[#9B9B8F] mt-1">Önce "Müşteriler" bölümünden müşteri ekleyin.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Başlangıç *</label>
                <input type="date" required data-testid="rental-form-start" className={inputCls} value={editing.start_date}
                  onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Bitiş *</label>
                <input type="date" required data-testid="rental-form-end" className={inputCls} value={editing.end_date}
                  onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Aylık Bedel (TL) *</label>
                <input type="number" required data-testid="rental-form-rate" className={inputCls} value={editing.monthly_rate}
                  onChange={(e) => setEditing({ ...editing, monthly_rate: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Durum</label>
                <select data-testid="rental-form-status" className={inputCls} value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {RENTAL_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Notlar</label>
              <textarea rows={2} className={inputCls} value={editing.notes || ""}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </div>
            <p className="text-[11px] text-[#9B9B8F]">Aynı araç için çakışan tarihlerde aktif kiralama veritabanı tarafından engellenir.</p>
            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                data-testid="rental-form-save"
                className="flex-1 py-3 rounded-sm bg-[#B09060] text-white font-semibold text-xs uppercase tracking-[0.12em] hover:bg-[#8F7648] transition-colors disabled:opacity-50"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
              <button type="button" onClick={() => setEditing(null)}
                className="px-6 py-3 rounded-sm border border-[#B09060]/30 text-[#404040] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors">
                Vazgeç
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
